import { useState, useEffect } from 'react';
import { Search as SearchIcon, Clock, CheckCircle, XCircle, Loader2, Play, History } from 'lucide-react';
import { searchAds, getSearchHistory } from '../api';
import { format } from 'date-fns';

function Search() {
  const [query, setQuery] = useState('dental');
  const [country, setCountry] = useState('GB');
  const [adType, setAdType] = useState('all');
  const [adActiveStatus, setAdActiveStatus] = useState('all');
  const [limit, setLimit] = useState(50);
  
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [error, setError] = useState(null);
  
  const [searchHistory, setSearchHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(null);

  useEffect(() => {
    loadSearchHistory();
  }, []);

  const loadSearchHistory = async () => {
    try {
      setLoadingHistory(true);
      setHistoryError(null);
      const response = await getSearchHistory({ limit: 10 });
      setSearchHistory(response.data?.data?.searches || []);
    } catch (err) {
      console.error('Failed to load search history:', err);
      setHistoryError(err.response?.data?.error || err.message || 'Failed to load history');
      setSearchHistory([]); // Set empty array on error
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    try {
      setSearching(true);
      setError(null);
      setSearchResult(null);
      
      const response = await searchAds({
        query,
        country,
        adType: adType !== 'all' ? adType : undefined,
        adActiveStatus: adActiveStatus !== 'all' ? adActiveStatus : undefined,
        limit,
      });
      
      setSearchResult(response.data.data);
      loadSearchHistory();
    } catch (err) {
      console.error('Search failed:', err);
      const errorMessage = err.response?.data?.error 
        || err.response?.data?.message 
        || err.message 
        || 'Search failed - please try again';
      setError(errorMessage);
    } finally {
      setSearching(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  useEffect(() => {
    document.title = 'Search | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Search Ads</h1>
        <p className="text-gray-600">Search Facebook Ad Library for dental competitor ads</p>
      </div>

      {/* Search Form */}
      <div className="card">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label htmlFor="search-query" className="block text-sm font-medium text-gray-700 mb-1">Search Query</label>
              <input
                id="search-query"
                type="text"
                className="input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g., dental implants, teeth whitening..."
                required
              />
            </div>
            
            <div>
              <label htmlFor="search-country" className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select
                id="search-country"
                className="input"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="GB">United Kingdom</option>
                <option value="US">United States</option>
                <option value="IE">Ireland</option>
                <option value="AU">Australia</option>
                <option value="CA">Canada</option>
              </select>
            </div>

            <div>
              <label htmlFor="search-ad-type" className="block text-sm font-medium text-gray-700 mb-1">Ad Type</label>
              <select
                id="search-ad-type"
                className="input"
                value={adType}
                onChange={(e) => setAdType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </div>

            <div>
              <label htmlFor="search-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                id="search-status"
                className="input"
                value={adActiveStatus}
                onChange={(e) => setAdActiveStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>

            <div>
              <label htmlFor="search-limit" className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
              <select
                id="search-limit"
                className="input"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                <option value={25}>25 ads</option>
                <option value={50}>50 ads</option>
                <option value={100}>100 ads</option>
                <option value={200}>200 ads</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-4 border-t">
            <p className="text-sm text-gray-500">
              Each search uses API credits. Limit results to conserve credits.
            </p>
            <button
              type="submit"
              disabled={searching || !query.trim()}
              className="btn-primary flex items-center gap-2"
            >
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <SearchIcon className="w-4 h-4" />
                  Search Ads
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Search Result */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {searchResult && (
        <div className="card bg-green-50 border-green-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-green-800 mb-1">Search Complete</h3>
              <div className="text-sm text-green-700 space-y-1">
                <p>Found <strong>{searchResult.adsFound}</strong> ads for "{searchResult.query}"</p>
                <p>Saved <strong>{searchResult.savedCount}</strong> new ads to database</p>
                {searchResult.updatedCount > 0 && (
                  <p>Updated <strong>{searchResult.updatedCount}</strong> existing ads with new metrics</p>
                )}
                <p>Analyzed <strong>{searchResult.analyzedCount}</strong> ads with AI</p>
                {searchResult.remainingCredits && (
                  <p className="text-green-600">Remaining API credits: {searchResult.remainingCredits}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search History */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold">Recent Searches</h2>
        </div>

        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-dental-600" />
          </div>
        ) : historyError ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700 text-sm">
            Could not load search history: {historyError}
          </div>
        ) : searchHistory.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No search history yet.</p>
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="md:hidden space-y-3">
              {searchHistory.map((search) => (
                <div key={search.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="font-medium truncate">{search.query}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {getStatusIcon(search.status)}
                      <span className="capitalize text-sm text-gray-600">{search.status}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                    <span>{search.country}</span>
                    {search.results_count != null && (
                      <span className="badge badge-info">{search.results_count} ads</span>
                    )}
                    {search.created_at && (
                      <span className="text-gray-500">
                        {(() => {
                          try {
                            return format(new Date(search.created_at), 'MMM d, yyyy HH:mm');
                          } catch {
                            return search.created_at;
                          }
                        })()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Query</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Country</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Results</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {searchHistory.map((search) => (
                    <tr key={search.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(search.status)}
                          <span className="capitalize text-sm">{search.status}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-medium">{search.query}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{search.country}</td>
                      <td className="py-3 px-4 text-sm">
                        {search.results_count != null ? (
                          <span className="badge badge-primary">{search.results_count} ads</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {search.created_at ? (() => {
                          try {
                            return format(new Date(search.created_at), 'MMM d, yyyy HH:mm');
                          } catch {
                            return search.created_at;
                          }
                        })() : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Search;
